import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import OpportunityLineItems from '@/components/OpportunityLineItems';
import { useAuth } from '@/App';
import {
  Briefcase,
  ArrowLeft,
  Edit2,
  Trash2,
  Save,
  X,
  Building2,
  User,
  IndianRupee,
  Calendar,
  Target,
  TrendingUp,
  Clock,
  Phone,
  Mail,
  Plus,
  CheckCircle,
  XCircle,
  Activity,
  FileText,
  Tag,
  Link as LinkIcon,
  ExternalLink,
  History,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  qualification: { label: 'Qualification', color: 'bg-blue-500', probability: 10 },
  discovery: { label: 'Discovery', color: 'bg-cyan-500', probability: 20 },
  meeting: { label: 'Meeting', color: 'bg-purple-500', probability: 40 },
  proposal: { label: 'Proposal', color: 'bg-amber-500', probability: 60 },
  negotiation: { label: 'Negotiation', color: 'bg-orange-500', probability: 80 },
  closed_won: { label: 'Closed Won', color: 'bg-green-500', probability: 100 },
  closed_lost: { label: 'Closed Lost', color: 'bg-red-500', probability: 0 }
};

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' }
];

import HistoryTab from '@/components/HistoryTab';
import PresalesRequestModal from '@/components/opportunity/PresalesRequestModal';

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(null); // 'won' or 'lost'
  const [lossReason, setLossReason] = useState('');
  // Sales Deal Form (SDF) state — captured on closure
  const [sdfWinReason, setSdfWinReason] = useState('');
  const [sdfCompetitor, setSdfCompetitor] = useState('');
  const [sdfDecisionMakers, setSdfDecisionMakers] = useState(''); // comma-separated in UI
  const [sdfCloseNotes, setSdfCloseNotes] = useState('');
  const [sdfSubmitting, setSdfSubmitting] = useState(false);
  const [sdfError, setSdfError] = useState('');
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activity_type: 'call',
    notes: '',
    outcome: '',
  });
  const [activitySubmitting, setActivitySubmitting] = useState(false);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showPresalesModal, setShowPresalesModal] = useState(false);

  const fetchOpportunity = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/opportunities/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOpportunity(data);
        setEditData(data);
      } else {
        navigate('/opportunities');
      }
    } catch (error) {
      console.error('Error fetching opportunity:', error);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchOpportunity();
    fetchProducts();
  }, [fetchOpportunity]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const [productsRes, oppProductsRes] = await Promise.all([
        fetch(`${API_URL}/api/products`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/opportunities/${id}/products`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (productsRes.ok) {
        const data = await productsRes.json();
        setAllProducts((data || []).filter(p => p.active !== false));
      }
      if (oppProductsRes.ok) {
        const data = await oppProductsRes.json();
        setLinkedProducts(data.products || []);
      }
    } catch (err) { console.error('Error fetching products:', err); }
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    setActivitySubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/activities/quick-log`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opportunity_id: id,
          lead_id: opportunity?.lead_id || null,
          activity_type: activityForm.activity_type,
          outcome: activityForm.outcome || activityForm.activity_type,
          notes: activityForm.notes,
        }),
      });
      if (response.ok) {
        setShowActivityModal(false);
        setActivityForm({ activity_type: 'call', notes: '', outcome: '' });
        fetchOpportunity();
      }
    } catch (error) {
      console.error('Error logging activity:', error);
    } finally {
      setActivitySubmitting(false);
    }
  };

  const handleToggleProduct = async (productId) => {
    const currentIds = linkedProducts.map(p => p.id);
    const newIds = currentIds.includes(productId) ? currentIds.filter(pid => pid !== productId) : [...currentIds, productId];
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/opportunities/${id}/products`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ linked_products: newIds }),
      });
      if (res.ok) {
        const data = await res.json();
        setLinkedProducts(data.products || []);
      }
    } catch (err) { console.error('Error updating products:', err); }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/opportunities/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editData.name,
          description: editData.description,
          deal_value: parseFloat(editData.deal_value) || 0,
          probability: parseInt(editData.probability) || 20,
          expected_close_date: editData.expected_close_date || null,
          priority: editData.priority,
          next_step: editData.next_step,
          competitor: editData.competitor
        })
      });

      if (response.ok) {
        setEditing(false);
        fetchOpportunity();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving opportunity:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    const reason = window.prompt('Why are you reopening this deal? (required for audit)');
    if (!reason || !reason.trim()) {
      toast.error('A reason is required to reopen a closed deal');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/opportunities/${id}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stage: 'negotiation', reason: reason.trim() }),
      });
      if (response.ok) {
        toast.success('Deal reopened (moved to Negotiation)');
        fetchOpportunity();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to reopen');
      }
    } catch (err) {
      console.error('Error reopening:', err);
      toast.error('Failed to reopen');
    }
  };

  const handleStageChange = async (newStage) => {
    if (newStage === 'closed_won') {
      setShowCloseModal('won');
    } else if (newStage === 'closed_lost') {
      setShowCloseModal('lost');
    } else {
      await updateStage(newStage);
    }
  };

  const updateStage = async (newStage, extras = null) => {
    try {
      setSdfSubmitting(true);
      setSdfError('');
      const token = localStorage.getItem('token');
      const body = { stage: newStage };
      if (extras && typeof extras === 'object') {
        Object.assign(body, extras);
      }

      const response = await fetch(`${API_URL}/api/opportunities/${id}/stage`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        setShowCloseModal(null);
        setLossReason('');
        setSdfWinReason('');
        setSdfCompetitor('');
        setSdfDecisionMakers('');
        setSdfCloseNotes('');
        fetchOpportunity();
      } else {
        setSdfError(result.detail || 'Failed to update stage');
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      setSdfError('Network error — please retry');
    } finally {
      setSdfSubmitting(false);
    }
  };

  const submitCloseModal = () => {
    if (showCloseModal === 'won') {
      const dms = sdfDecisionMakers.split(',').map(s => s.trim()).filter(Boolean);
      if (!sdfWinReason.trim()) { setSdfError('Win reason is required'); return; }
      if (!sdfCompetitor.trim()) { setSdfError('Competitor is required (enter "None" if no competitor)'); return; }
      if (dms.length === 0) { setSdfError('At least one decision maker is required'); return; }
      updateStage('closed_won', {
        win_reason: sdfWinReason.trim(),
        competitor: sdfCompetitor.trim(),
        decision_makers: dms,
        close_notes: sdfCloseNotes.trim() || undefined,
      });
    } else if (showCloseModal === 'lost') {
      if (!lossReason.trim()) { setSdfError('Loss reason is required'); return; }
      updateStage('closed_lost', {
        loss_reason: lossReason.trim(),
        competitor: sdfCompetitor.trim() || undefined,
        close_notes: sdfCloseNotes.trim() || undefined,
      });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this opportunity? This action cannot be undone.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/opportunities/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        navigate('/opportunities');
      }
    } catch (error) {
      console.error('Error deleting opportunity:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 text-slate-500 dark:text-slate-400">
        Loading opportunity...
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-500 dark:text-slate-400">Opportunity not found</p>
        <button
          onClick={() => navigate('/opportunities')}
          className="mt-4 text-purple-600 dark:text-purple-400 hover:underline"
        >
          Back to Opportunities
        </button>
      </div>
    );
  }

  const stageConfig = STAGE_CONFIG[opportunity.stage] || STAGE_CONFIG.qualification;
  const isClosed = ['closed_won', 'closed_lost'].includes(opportunity.stage);
  const isLocked = isClosed && !isAdmin;

  return (
    <div className="space-y-6" data-testid="opportunity-detail">
      {/* Closed deal banner */}
      {isClosed && (
        <div
          className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
            opportunity.stage === 'closed_won'
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
          }`}
          data-testid="closed-deal-banner"
        >
          {opportunity.stage === 'closed_won' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          <div className="flex-1 text-sm">
            <span className="font-semibold">
              Deal {opportunity.stage === 'closed_won' ? 'Won' : 'Lost'} — read-only.
            </span>{' '}
            <span className="opacity-80">
              {isAdmin
                ? 'As an Admin you can still edit or Reopen this deal for correction.'
                : 'This deal is locked. Only an Admin can reopen or edit it.'}
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/opportunities')}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
          <div>
            {editing ? (
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                className="text-2xl font-bold bg-transparent border-b-2 border-purple-500 text-slate-900 dark:text-white focus:outline-none"
              />
            ) : (
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {opportunity.name}
              </h1>
            )}
            <div className="flex items-center gap-3 mt-2">
              <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${stageConfig.color}`}>
                {stageConfig.label}
              </span>
              {opportunity.company_name && (
                <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400 text-sm">
                  <Building2 className="w-4 h-4" />
                  {opportunity.company_name}
                </span>
              )}
              {opportunity.lead_name && (
                <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400 text-sm">
                  <User className="w-4 h-4" />
                  {opportunity.lead_name}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button
                onClick={() => {
                  setEditing(false);
                  setEditData(opportunity);
                }}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              {opportunity.lead_id && !isClosed && (
                <button
                  onClick={() => setShowPresalesModal(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                  data-testid="request-presales-btn"
                  title="Create a pre-sales request (demo, POC, tech eval) linked to this opportunity"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                  Pre-Sales Help
                </button>
              )}
              {isClosed && isAdmin && (
                <button
                  onClick={handleReopen}
                  className="flex items-center gap-2 px-4 py-2 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                  data-testid="reopen-opportunity-btn"
                  title="Admin-only: reopen this closed deal for editing"
                >
                  <History className="w-4 h-4" />
                  Reopen
                </button>
              )}
              <button
                onClick={() => !isLocked && setEditing(true)}
                disabled={isLocked}
                title={isLocked ? 'This deal is closed. Only Admin can edit.' : 'Edit opportunity'}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isLocked
                    ? 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                    : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
                data-testid="edit-opportunity-btn"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Deal Info Card */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-500" />
              Deal Information
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">Deal Value</label>
                {editing ? (
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="number"
                      value={editData.deal_value}
                      onChange={(e) => setEditData(prev => ({ ...prev, deal_value: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white"
                    />
                  </div>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="opp-tcv">
                      {formatCurrency(opportunity.deal_value)}
                    </p>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">TCV</span>
                  </>
                )}
              </div>

              {!editing && (opportunity.acv || 0) > 0 && (
                <div data-testid="opp-acv-tile">
                  <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">Annual Contract Value</label>
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {formatCurrency(opportunity.acv)}
                  </p>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">ACV (annualized)</span>
                </div>
              )}

              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">Probability</label>
                {editing ? (
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editData.probability}
                    onChange={(e) => setEditData(prev => ({ ...prev, probability: e.target.value }))}
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white"
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-full h-3 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${opportunity.probability >= 70 ? 'bg-green-500' : opportunity.probability >= 40 ? 'bg-amber-500' : 'bg-slate-400'}`}
                        style={{ width: `${opportunity.probability}%` }}
                      />
                    </div>
                    <span className="text-lg font-semibold text-slate-900 dark:text-white">{opportunity.probability}%</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">Expected Close</label>
                {editing ? (
                  <input
                    type="date"
                    value={editData.expected_close_date ? editData.expected_close_date.split('T')[0] : ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, expected_close_date: e.target.value }))}
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white"
                  />
                ) : (
                  <p className="text-lg font-medium text-slate-900 dark:text-white flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {formatDate(opportunity.expected_close_date)}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">Weighted Value</label>
                <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                  {formatCurrency((opportunity.deal_value || 0) * (opportunity.probability || 0) / 100)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-6">
              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">Priority</label>
                {editing ? (
                  <select
                    value={editData.priority}
                    onChange={(e) => setEditData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white"
                  >
                    {PRIORITY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize
                    ${opportunity.priority === 'critical' ? 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-300' :
                      opportunity.priority === 'high' ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/50 dark:text-orange-300' :
                      opportunity.priority === 'medium' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300' :
                      'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                    {opportunity.priority || 'Medium'}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">Source</label>
                <p className="text-slate-900 dark:text-white capitalize">
                  {opportunity.source?.replace('_', ' ') || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Stage Pipeline */}
          {!isClosed && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                Stage Pipeline
              </h2>

              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {Object.entries(STAGE_CONFIG)
                  .filter(([key]) => !['closed_won', 'closed_lost'].includes(key))
                  .map(([key, config], index) => (
                    <button
                      key={key}
                      onClick={() => handleStageChange(key)}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        opportunity.stage === key
                          ? `${config.color} text-white`
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {config.label}
                    </button>
                  ))}
                
                <div className="flex-shrink-0 h-8 w-px bg-slate-200 dark:bg-slate-700 mx-2" />
                
                <button
                  onClick={() => handleStageChange('closed_won')}
                  className="flex-shrink-0 flex items-center gap-1 px-4 py-2 bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 rounded-lg text-sm font-medium hover:bg-green-200 dark:hover:bg-green-900/70 transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  Won
                </button>
                <button
                  onClick={() => handleStageChange('closed_lost')}
                  className="flex-shrink-0 flex items-center gap-1 px-4 py-2 bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/70 transition-colors"
                >
                  <XCircle className="w-4 h-4" />
                  Lost
                </button>
              </div>
            </div>
          )}

          {/* Next Step */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-500" />
              Next Step
            </h2>
            {editing ? (
              <input
                type="text"
                value={editData.next_step || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, next_step: e.target.value }))}
                placeholder="What's the next action for this opportunity?"
                className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400"
              />
            ) : (
              <p className="text-slate-700 dark:text-slate-300">
                {opportunity.next_step || 'No next step defined'}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              Description
            </h2>
            {editing ? (
              <textarea
                value={editData.description || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                placeholder="Add a description..."
                className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 resize-none"
              />
            ) : (
              <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                {opportunity.description || 'No description'}
              </p>
            )}
          </div>

          {/* Products & SKUs — Phase 2 line items */}
          <OpportunityLineItems opportunityId={id} readOnly={isLocked} />

          {/* Activities */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-500" />
                Activities
              </h2>
              <button
                onClick={() => setShowActivityModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded-lg text-sm hover:bg-purple-200 dark:hover:bg-purple-900/70 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Log Activity
              </button>
            </div>

            {opportunity.activities && opportunity.activities.length > 0 ? (
              <div className="space-y-3">
                {opportunity.activities.map(activity => (
                  <div key={activity.id} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                      <Activity className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white capitalize">
                        {activity.activity_type?.replace('_', ' ')}
                      </p>
                      {activity.notes && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
                          {activity.notes}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                        {formatDateTime(activity.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 dark:text-slate-400 text-center py-4">
                No activities yet
              </p>
            )}
          </div>
        </div>

        {/* Right Column - Side Info */}
        <div className="space-y-6">
          {/* Stage History */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-purple-500" />
              Stage History
            </h2>
            
            {opportunity.stage_history && opportunity.stage_history.length > 0 ? (
              <div className="space-y-3">
                {opportunity.stage_history.slice().reverse().map((entry) => (
                  <div key={entry.changed_at || entry.stage} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${STAGE_CONFIG[entry.stage]?.color || 'bg-slate-400'}`} />
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {STAGE_CONFIG[entry.stage]?.label || entry.stage}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {formatDateTime(entry.timestamp)}
                      </p>
                      {entry.user_name && (
                        <p className="text-xs text-slate-400 dark:text-slate-500">
                          by {entry.user_name}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 dark:text-slate-400 text-sm">No history</p>
            )}
          </div>

          {/* Related Lead */}
          {opportunity.lead && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-purple-500" />
                Related Lead
              </h2>
              
              <div 
                onClick={() => navigate(`/leads/${opportunity.lead.id}`)}
                className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <p className="font-medium text-slate-900 dark:text-white">
                  {opportunity.lead.name}
                </p>
                {opportunity.lead.organization && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {opportunity.lead.organization}
                  </p>
                )}
                {opportunity.lead.email && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-2">
                    <Mail className="w-3 h-3" />
                    {opportunity.lead.email}
                  </p>
                )}
                {opportunity.lead.phone && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {opportunity.lead.phone}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Related Company */}
          {opportunity.company && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-500" />
                Related Company
              </h2>
              
              <div 
                onClick={() => navigate(`/companies/${opportunity.company.id}`)}
                className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <p className="font-medium text-slate-900 dark:text-white">
                  {opportunity.company.name}
                </p>
                {opportunity.company.industry && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {opportunity.company.industry}
                  </p>
                )}
                {opportunity.company.website && (
                  <a 
                    href={opportunity.company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm text-purple-600 dark:text-purple-400 flex items-center gap-1 mt-2 hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Website
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Loss Reason (if closed lost) */}
          {opportunity.stage === 'closed_lost' && opportunity.loss_reason && (
            <div className="bg-red-50 dark:bg-red-900/30 rounded-xl border border-red-200 dark:border-red-800 p-6">
              <h2 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                Loss Reason
              </h2>
              <p className="text-red-600 dark:text-red-400">
                {opportunity.loss_reason}
              </p>
            </div>
          )}

          {/* Meta Info */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase mb-4">
              Details
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Created</span>
                <span className="text-slate-900 dark:text-white">{formatDate(opportunity.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Updated</span>
                <span className="text-slate-900 dark:text-white">{formatDate(opportunity.updated_at)}</span>
              </div>
              {opportunity.closed_at && (
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Closed</span>
                  <span className="text-slate-900 dark:text-white">{formatDate(opportunity.closed_at)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Owner</span>
                <span className="text-slate-900 dark:text-white">{opportunity.user_name || '-'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Close Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              {showCloseModal === 'won' ? (
                <>
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  Mark as Won
                </>
              ) : (
                <>
                  <XCircle className="w-6 h-6 text-red-500" />
                  Mark as Lost
                </>
              )}
            </h2>

            {showCloseModal === 'won' ? (
              <div className="mb-6 space-y-4" data-testid="sdf-won-form">
                <p className="text-slate-600 dark:text-slate-400">
                  Deal value {formatCurrency(opportunity.deal_value)}. Capture Sales Deal Form details before closing.
                </p>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Why did we win? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={sdfWinReason}
                    onChange={(e) => { setSdfWinReason(e.target.value); setSdfError(''); }}
                    placeholder="E.g. Better pricing, stronger feature match, faster deployment..."
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 resize-none text-sm"
                    data-testid="sdf-win-reason-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Competitor <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={sdfCompetitor}
                    onChange={(e) => { setSdfCompetitor(e.target.value); setSdfError(''); }}
                    placeholder='Competing vendor (enter "None" if none)'
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 text-sm"
                    data-testid="sdf-competitor-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Decision Makers <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={sdfDecisionMakers}
                    onChange={(e) => { setSdfDecisionMakers(e.target.value); setSdfError(''); }}
                    placeholder="Comma-separated names — e.g. Priya Sharma, CFO; Arjun Mehta, CTO"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 text-sm"
                    data-testid="sdf-decision-makers-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Close Notes <span className="text-slate-400">(optional)</span>
                  </label>
                  <textarea
                    value={sdfCloseNotes}
                    onChange={(e) => setSdfCloseNotes(e.target.value)}
                    placeholder="Any hand-off notes for CSM / finance..."
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 resize-none text-sm"
                    data-testid="sdf-close-notes-input"
                  />
                </div>
              </div>
            ) : (
              <div className="mb-6 space-y-4" data-testid="sdf-lost-form">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Loss Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={lossReason}
                    onChange={(e) => { setLossReason(e.target.value); setSdfError(''); }}
                    placeholder="Why was this opportunity lost?"
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 resize-none text-sm"
                    data-testid="sdf-loss-reason-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Competitor <span className="text-slate-400">(if lost to one)</span>
                  </label>
                  <input
                    type="text"
                    value={sdfCompetitor}
                    onChange={(e) => setSdfCompetitor(e.target.value)}
                    placeholder="Competing vendor name"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 text-sm"
                    data-testid="sdf-lost-competitor-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Close Notes <span className="text-slate-400">(optional)</span>
                  </label>
                  <textarea
                    value={sdfCloseNotes}
                    onChange={(e) => setSdfCloseNotes(e.target.value)}
                    placeholder="Lessons learned / follow-up notes"
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 resize-none text-sm"
                    data-testid="sdf-lost-close-notes-input"
                  />
                </div>
              </div>
            )}

            {sdfError && (
              <div
                className="mb-3 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm"
                data-testid="sdf-error-banner"
              >
                {sdfError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCloseModal(null);
                  setLossReason('');
                  setSdfWinReason('');
                  setSdfCompetitor('');
                  setSdfDecisionMakers('');
                  setSdfCloseNotes('');
                  setSdfError('');
                }}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                data-testid="sdf-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submitCloseModal}
                disabled={sdfSubmitting}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  showCloseModal === 'won'
                    ? 'bg-green-600 hover:bg-green-700 text-white disabled:bg-green-400'
                    : 'bg-red-600 hover:bg-red-700 text-white disabled:bg-red-400'
                }`}
                data-testid="sdf-submit-btn"
              >
                {sdfSubmitting ? 'Submitting...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="opp-activity-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Log Activity</h3>
            <form onSubmit={handleActivitySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Activity Type</label>
                <select
                  value={activityForm.activity_type}
                  onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  data-testid="opp-activity-type-select"
                >
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="note">Note</option>
                  <option value="linkedin_message">LinkedIn Message</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Outcome</label>
                <select
                  value={activityForm.outcome}
                  onChange={(e) => setActivityForm({ ...activityForm, outcome: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  data-testid="opp-activity-outcome-select"
                >
                  <option value="">Select outcome...</option>
                  <option value="interested">Interested</option>
                  <option value="callback">Callback Requested</option>
                  <option value="not_interested">Not Interested</option>
                  <option value="voicemail">Voicemail</option>
                  <option value="no_answer">No Answer</option>
                  <option value="meeting_set">Meeting Set</option>
                  <option value="proposal_sent">Proposal Sent</option>
                  <option value="demo_done">Demo Done</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</label>
                <textarea
                  value={activityForm.notes}
                  onChange={(e) => setActivityForm({ ...activityForm, notes: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  placeholder="Add notes about this activity..."
                  data-testid="opp-activity-notes-textarea"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowActivityModal(false); setActivityForm({ activity_type: 'call', notes: '', outcome: '' }); }}
                  className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={activitySubmitting}
                  className="px-4 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors disabled:opacity-50"
                  data-testid="opp-activity-submit-btn"
                >
                  {activitySubmitting ? 'Logging...' : 'Log Activity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pre-Sales Request Modal */}
      {showPresalesModal && (
        <PresalesRequestModal
          opportunity={opportunity}
          onClose={() => setShowPresalesModal(false)}
          onSuccess={(req) => {
            setShowPresalesModal(false);
            toast.success(`Pre-sales request #${req.id?.slice(0, 8) || ''} created — SLA ${req.sla?.response_target_hours || 8}h`);
          }}
        />
      )}

      {/* History — audit trail of stage changes, edits, reopens */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6" data-testid="opp-history-section">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">History</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">Every stage change, value edit, reassignment, and reopen on this deal.</p>
        <HistoryTab collection="opportunities" docId={id} />
      </div>
    </div>
  );
}
